import { HomepageComponentKeyType } from 'features/MultiCategory/@types/homeTab'

/**
 * Shows the "Min. 1, maks. 10 item (0 dipilih)" hint under each linked-record picker.
 * Off for now; the limits still cap adding and block the save.
 */
export const IS_SELECTION_LIMIT_HINT_ENABLED = false

/** Component keys the picker, drawer configs and catalog options share. */
export const HOMEPAGE_COMPONENT_KEY = {
  ALL_CATEGORY: 'all-category',
  BANNER: 'banner',
  BANNER_2X2: 'banner-2x2',
  BANNER_THEMATIC: 'banner-thematic',
  BANNER_CATEGORY_HIGHLIGHT: 'banner-category-highlight',
  BIG_CATALOG: 'big-catalog',
  BRAND_CATALOG: 'brand-catalog',
  CATALOG: 'catalog',
  CATALOG_VERTICAL: 'catalog-vertical',
  CATEGORY_SPESIFIK: 'category-spesifik',
  FLASH_SALE: 'flash-sale',
  FLEXI_COMBO: 'flexi-combo',
  MILESTONE: 'milestone',
  PRODUCT_CAROUSEL: 'product-carousel',
  PRODUCT_CATEGORY_HIGHLIGHT: 'product-category-highlight',
  PWP: 'pwp',
  SEND_AS_GIFT: 'send-as-gift',
} as const satisfies Record<string, HomepageComponentKeyType>

/**
 * Aliases for `code` values that do not become a known key by replacing `_`
 * with `-` — mostly Banner Regular, which the API ships as plain `banner`.
 */
const HOMEPAGE_COMPONENT_KEY_BY_CODE: Record<string, HomepageComponentKeyType> = {
  banner: HOMEPAGE_COMPONENT_KEY.BANNER,
  banner_regular: HOMEPAGE_COMPONENT_KEY.BANNER,
  all_category: HOMEPAGE_COMPONENT_KEY.ALL_CATEGORY,
  banner_2x2: HOMEPAGE_COMPONENT_KEY.BANNER_2X2,
  banner_thematic: HOMEPAGE_COMPONENT_KEY.BANNER_THEMATIC,
  banner_category_highlight: HOMEPAGE_COMPONENT_KEY.BANNER_CATEGORY_HIGHLIGHT,
  big_catalog: HOMEPAGE_COMPONENT_KEY.BIG_CATALOG,
  brand_catalog: HOMEPAGE_COMPONENT_KEY.BRAND_CATALOG,
  catalog: HOMEPAGE_COMPONENT_KEY.CATALOG,
  catalog_vertical: HOMEPAGE_COMPONENT_KEY.CATALOG_VERTICAL,
  /* The API spells this one the other way round. */
  vertical_catalog: HOMEPAGE_COMPONENT_KEY.CATALOG_VERTICAL,
  category_specific: HOMEPAGE_COMPONENT_KEY.CATEGORY_SPESIFIK,
  category_spesifik: HOMEPAGE_COMPONENT_KEY.CATEGORY_SPESIFIK,
  flash_sale: HOMEPAGE_COMPONENT_KEY.FLASH_SALE,
  flexi_combo: HOMEPAGE_COMPONENT_KEY.FLEXI_COMBO,
  milestone: HOMEPAGE_COMPONENT_KEY.MILESTONE,
  product_carousel: HOMEPAGE_COMPONENT_KEY.PRODUCT_CAROUSEL,
  product_category_highlight: HOMEPAGE_COMPONENT_KEY.PRODUCT_CATEGORY_HIGHLIGHT,
  pwp: HOMEPAGE_COMPONENT_KEY.PWP,
  send_as_gift: HOMEPAGE_COMPONENT_KEY.SEND_AS_GIFT,
}

const HOMEPAGE_COMPONENT_KEYS: HomepageComponentKeyType[] = Object.values(HOMEPAGE_COMPONENT_KEY)

/**
 * `code` from the component-types API → the local key the drawer is configured
 * against. Falls back to the snake → kebab form of the code, so a component the
 * API adds later maps itself as long as its code matches the key. Codes with no
 * match still show in the picker, but have no form yet.
 */
export const getHomepageComponentKeyByCode = (code: string): HomepageComponentKeyType | '' => {
  const normalized = code.toLowerCase().replace(/_/g, '-')

  return (
    HOMEPAGE_COMPONENT_KEY_BY_CODE[code.toLowerCase()] ||
    HOMEPAGE_COMPONENT_KEYS.find((key) => key === normalized) ||
    ''
  )
}

/** Components that link an existing dynamic channel instead of their own content. */
export const DYNAMIC_CHANNEL_COMPONENT_KEYS: HomepageComponentKeyType[] = [
  HOMEPAGE_COMPONENT_KEY.BANNER_2X2,
  HOMEPAGE_COMPONENT_KEY.PRODUCT_CAROUSEL,
  HOMEPAGE_COMPONENT_KEY.BANNER_CATEGORY_HIGHLIGHT,
  HOMEPAGE_COMPONENT_KEY.PRODUCT_CATEGORY_HIGHLIGHT,
]

/** Components that link catalogues, whatever their layout. */
export const CATALOG_COMPONENT_KEYS: HomepageComponentKeyType[] = [
  HOMEPAGE_COMPONENT_KEY.CATALOG,
  HOMEPAGE_COMPONENT_KEY.BIG_CATALOG,
  HOMEPAGE_COMPONENT_KEY.BRAND_CATALOG,
  HOMEPAGE_COMPONENT_KEY.CATALOG_VERTICAL,
]

/** `catalog_type_id` per catalogue layout, from the catalogue-type master. */
export const CATALOG_TYPE_ID = {
  KATALOG: 1,
  ICON: 2,
  BIG_KATALOG: 3,
  VERTICAL: 4,
  BRAND: 5,
} as const

/**
 * `dynamic_channel_type_id` per component, from `/api/dynamic-channel/type`.
 * Scopes each dynamic-channel drawer to its own channel type.
 */
export const DYNAMIC_CHANNEL_TYPE_ID = {
  PRODUCT_CAROUSEL: 1,
  BANNER_2X2: 6,
  BANNER_CATEGORY_HIGHLIGHT: 7,
  PRODUCT_CATEGORY_HIGHLIGHT: 8,
} as const

/** Columns in the picker grid; rows are derived from the fetched list. */
export const HOMEPAGE_COMPONENT_PICKER_COLUMNS = 3

/** Badge shown on a component type the API flags as `all_tab_only`. */
export const SEMUA_ONLY_BADGE_LABEL = 'Khusus Tab Semua'

/** Tab slot the "Semua" tab sits in — the only slot where `all_tab_only` types are addable. */
export const SEMUA_TAB_SLOT = 1

/** Badges shown when Banner Regular / Banner Thematic locks the other one out. */
export const BANNER_REGULAR_SELECTED_BADGE_LABEL = 'Banner Regular Terpilih'
export const BANNER_THEMATIC_SELECTED_BADGE_LABEL = 'Banner Thematic Terpilih'
/** Badge on a type whose `max_component_selected` the tab already reached. */
export const getMaxComponentReachedBadgeLabel = (max: number) => `Maks. ${max} Komponen`

/** Redirection targets offered by the All Category and Catalog components. */
export const REDIRECTION_OPTIONS = [{ name: 'Category', value: 'category' }]

export const TARGETING_TYPE_OPTIONS = [
  { name: 'Pilih Targeting Type', value: '' },
  { name: 'Segment', value: 'segment' },
  { name: 'User Tier', value: 'user-tier' },
]
